import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { 
  Plus, Send, CheckCircle2, XCircle, Clock, AlertCircle,
  Target, Megaphone, Calendar, DollarSign, Users, MessageSquare,
  RefreshCw, ChevronRight, FileText, ExternalLink
} from "lucide-react";
import api from "../api";

const MarketingRequestsPage = () => {
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const isMarketingTeam = hasRole(['admin', 'marketing_manager']);
  
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [dashboardStats, setDashboardStats] = useState(null);
  const [segments, setSegments] = useState([]);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    objective: "lead_generation",
    target_segments: [],
    target_audience_description: "",
    requested_channels: [],
    suggested_budget: "",
    desired_start_date: "",
    desired_end_date: "",
    urgency: "normal",
    supporting_notes: ""
  });
  
  const [reviewData, setReviewData] = useState({
    action: "approve",
    feedback: "",
    approved_budget: "",
    estimated_completion: ""
  });

  const objectiveLabels = {
    lead_generation: "Lead Generation",
    brand_awareness: "Brand Awareness",
    customer_retention: "Customer Retention",
    product_launch: "Product Launch",
    event_promotion: "Event Promotion",
    sales_support: "Sales Support",
    upsell_cross_sell: "Upsell/Cross-sell",
    re_engagement: "Re-engagement",
    other: "Other"
  };

  const channelOptions = [
    { value: "email", label: "Email Campaign" },
    { value: "social_organic", label: "Social Media (Organic)" },
    { value: "social_paid", label: "Social Media (Paid)" },
    { value: "ppc", label: "PPC / Search Ads" },
    { value: "display", label: "Display Advertising" },
    { value: "content", label: "Content Marketing" },
    { value: "webinar", label: "Webinar" },
    { value: "trade_show", label: "Trade Show" },
    { value: "direct_mail", label: "Direct Mail" },
    { value: "other", label: "Other" }
  ];

  const statusConfig = {
    submitted: { label: "Submitted", color: "bg-blue-100 text-blue-700", icon: Send },
    in_review: { label: "In Review", color: "bg-yellow-100 text-yellow-700", icon: Clock },
    approved: { label: "Approved", color: "bg-green-100 text-green-700", icon: CheckCircle2 },
    rejected: { label: "Rejected", color: "bg-red-100 text-red-700", icon: XCircle },
    in_progress: { label: "In Progress", color: "bg-purple-100 text-purple-700", icon: RefreshCw },
    completed: { label: "Completed", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
    cancelled: { label: "Cancelled", color: "bg-slate-100 text-slate-700", icon: XCircle }
  };

  const urgencyConfig = {
    low: { label: "Low", color: "bg-slate-100 text-slate-600" },
    normal: { label: "Normal", color: "bg-blue-100 text-blue-600" },
    high: { label: "High", color: "bg-orange-100 text-orange-600" },
    urgent: { label: "Urgent", color: "bg-red-100 text-red-600" }
  };

  // Fetch requests
  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const params = statusFilter !== "all" ? { status: statusFilter } : {};
      const [requestsRes, statsRes, segmentsRes] = await Promise.all([
        api.get("/marketing-requests", { params }),
        api.get("/marketing-requests/dashboard/stats"),
        api.get("/segments")
      ]);
      
      setRequests(requestsRes.data.requests || []);
      setStatusCounts(requestsRes.data.status_counts || {});
      setDashboardStats(statsRes.data);
      setSegments(segmentsRes.data?.segments || segmentsRes.data || []);
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Create request
  const handleCreateRequest = async () => {
    try {
      const data = {
        ...formData,
        suggested_budget: formData.suggested_budget ? parseFloat(formData.suggested_budget) : null
      };
      
      await api.post("/marketing-requests", data);
      toast.success("Marketing request submitted!");
      setShowCreateDialog(false);
      setFormData({
        title: "", description: "", objective: "lead_generation",
        target_segments: [], target_audience_description: "",
        requested_channels: [], suggested_budget: "",
        desired_start_date: "", desired_end_date: "",
        urgency: "normal", supporting_notes: ""
      });
      fetchRequests();
    } catch (error) {
      toast.error("Failed to submit request");
    }
  };

  // Review request
  const handleReviewRequest = async () => {
    if (!selectedRequest) return;
    try {
      const data = {
        action: reviewData.action,
        feedback: reviewData.feedback,
        approved_budget: reviewData.approved_budget ? parseFloat(reviewData.approved_budget) : null,
        estimated_completion: reviewData.estimated_completion || null
      };
      
      await api.post(`/marketing-requests/${selectedRequest.id}/review`, data);
      toast.success(`Request ${reviewData.action === 'approve' ? 'approved' : reviewData.action === 'reject' ? 'rejected' : 'updated'}!`);
      setShowReviewDialog(false);
      setReviewData({ action: "approve", feedback: "", approved_budget: "", estimated_completion: "" });
      fetchRequests();
    } catch (error) {
      toast.error("Failed to process review");
    }
  };

  // View request detail
  const viewRequestDetail = async (request) => {
    try {
      const response = await api.get(`/marketing-requests/${request.id}`);
      setSelectedRequest(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error("Failed to load request details");
    }
  };

  // Toggle channel selection
  const toggleChannel = (channel) => {
    if (formData.requested_channels.includes(channel)) {
      setFormData({...formData, requested_channels: formData.requested_channels.filter(c => c !== channel)});
    } else {
      setFormData({...formData, requested_channels: [...formData.requested_channels, channel]});
    }
  };

  // Toggle segment selection
  const toggleSegment = (segmentId) => {
    if (formData.target_segments.includes(segmentId)) {
      setFormData({...formData, target_segments: formData.target_segments.filter(s => s !== segmentId)});
    } else {
      setFormData({...formData, target_segments: [...formData.target_segments, segmentId]});
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="marketing-requests-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Marketing Requests</h1>
          <p className="text-muted-foreground">
            {isMarketingTeam 
              ? "Review and manage marketing requests from the sales team"
              : "Request marketing activities for your campaigns"
            }
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="new-request-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Request
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className={statusFilter === "all" ? "ring-2 ring-blue-500" : "cursor-pointer hover:bg-slate-50"} onClick={() => setStatusFilter("all")}>
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold">{dashboardStats?.counts?.total || 0}</p>
            <p className="text-sm text-muted-foreground">Total Requests</p>
          </CardContent>
        </Card>
        <Card className={`${statusFilter === "submitted" ? "ring-2 ring-blue-500" : "cursor-pointer hover:bg-slate-50"} bg-blue-50`} onClick={() => setStatusFilter("submitted")}>
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{statusCounts.submitted || 0}</p>
            <p className="text-sm text-blue-600">Pending Review</p>
          </CardContent>
        </Card>
        <Card className={`${statusFilter === "approved" ? "ring-2 ring-green-500" : "cursor-pointer hover:bg-slate-50"} bg-green-50`} onClick={() => setStatusFilter("approved")}>
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-green-600">{statusCounts.approved || 0}</p>
            <p className="text-sm text-green-600">Approved</p>
          </CardContent>
        </Card>
        <Card className={`${statusFilter === "in_progress" ? "ring-2 ring-purple-500" : "cursor-pointer hover:bg-slate-50"} bg-purple-50`} onClick={() => setStatusFilter("in_progress")}>
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-purple-600">{statusCounts.in_progress || 0}</p>
            <p className="text-sm text-purple-600">In Progress</p>
          </CardContent>
        </Card>
        <Card className={`${statusFilter === "completed" ? "ring-2 ring-emerald-500" : "cursor-pointer hover:bg-slate-50"} bg-emerald-50`} onClick={() => setStatusFilter("completed")}>
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">{statusCounts.completed || 0}</p>
            <p className="text-sm text-emerald-600">Completed</p>
          </CardContent>
        </Card>
      </div>

      {/* Urgent Requests */}
      {dashboardStats?.urgent_requests?.length > 0 && (
        <Card className="border-orange-300 bg-orange-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-orange-700 flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Urgent Requests
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {dashboardStats.urgent_requests.slice(0, 3).map((req) => (
                <div 
                  key={req.id} 
                  className="flex items-center justify-between p-2 bg-white rounded cursor-pointer hover:bg-orange-100"
                  onClick={() => viewRequestDetail(req)}
                >
                  <div className="flex items-center gap-2">
                    <Badge className={urgencyConfig[req.urgency]?.color}>{req.urgency}</Badge>
                    <span className="font-medium">{req.title}</span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Requests List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>
              {statusFilter === "all" ? "All Requests" : statusConfig[statusFilter]?.label || "Requests"}
            </CardTitle>
            {statusFilter !== "all" && (
              <Button variant="ghost" size="sm" onClick={() => setStatusFilter("all")}>
                Clear Filter
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <div className="text-center py-8">
              <Megaphone className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-semibold">No requests found</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {statusFilter !== "all" ? "Try clearing the filter" : "Create your first marketing request"}
              </p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                New Request
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {requests.map((request) => {
                const StatusIcon = statusConfig[request.status]?.icon || Clock;
                return (
                  <div 
                    key={request.id}
                    className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer"
                    onClick={() => viewRequestDetail(request)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={statusConfig[request.status]?.color}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusConfig[request.status]?.label}
                          </Badge>
                          <Badge className={urgencyConfig[request.urgency]?.color}>
                            {request.urgency}
                          </Badge>
                          <Badge variant="outline">{objectiveLabels[request.objective]}</Badge>
                        </div>
                        <h3 className="font-semibold">{request.title}</h3>
                        <p className="text-sm text-muted-foreground line-clamp-1">{request.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                          <span>#{request.request_number}</span>
                          <span>By: {request.requester_name || request.requester?.first_name}</span>
                          {request.suggested_budget && (
                            <span>Budget: ${request.suggested_budget.toLocaleString()}</span>
                          )}
                          <span>{new Date(request.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isMarketingTeam && request.status === "submitted" && (
                          <Button 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedRequest(request);
                              setShowReviewDialog(true);
                            }}
                          >
                            Review
                          </Button>
                        )}
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Request Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Marketing Request</DialogTitle>
            <DialogDescription>
              Request marketing activities to support your sales efforts
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Request Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="e.g., Q1 Lead Generation for Enterprise Segment"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Describe what you need and why..."
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Campaign Objective</Label>
                <Select
                  value={formData.objective}
                  onValueChange={(v) => setFormData({...formData, objective: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(objectiveLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Urgency</Label>
                <Select
                  value={formData.urgency}
                  onValueChange={(v) => setFormData({...formData, urgency: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Target Segments</Label>
              <div className="flex flex-wrap gap-2">
                {segments.map((seg) => (
                  <Badge
                    key={seg.id}
                    variant={formData.target_segments.includes(seg.id) ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => toggleSegment(seg.id)}
                  >
                    {seg.label || seg.name}
                  </Badge>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Target Audience Description</Label>
              <Textarea
                value={formData.target_audience_description}
                onChange={(e) => setFormData({...formData, target_audience_description: e.target.value})}
                placeholder="Describe your ideal target audience..."
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Requested Channels</Label>
              <div className="flex flex-wrap gap-2">
                {channelOptions.map((ch) => (
                  <Badge
                    key={ch.value}
                    variant={formData.requested_channels.includes(ch.value) ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => toggleChannel(ch.value)}
                  >
                    {ch.label}
                  </Badge>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Suggested Budget ($)</Label>
                <Input
                  type="number"
                  value={formData.suggested_budget}
                  onChange={(e) => setFormData({...formData, suggested_budget: e.target.value})}
                  placeholder="5000"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Desired Start Date</Label>
                <Input
                  type="date"
                  value={formData.desired_start_date}
                  onChange={(e) => setFormData({...formData, desired_start_date: e.target.value})}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Desired End Date</Label>
                <Input
                  type="date"
                  value={formData.desired_end_date}
                  onChange={(e) => setFormData({...formData, desired_end_date: e.target.value})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Supporting Notes</Label>
              <Textarea
                value={formData.supporting_notes}
                onChange={(e) => setFormData({...formData, supporting_notes: e.target.value})}
                placeholder="Any additional context, links, or attachments..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateRequest} disabled={!formData.title || !formData.description}>
              <Send className="h-4 w-4 mr-2" />
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Request</DialogTitle>
            <DialogDescription>
              {selectedRequest?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Action</Label>
              <Select
                value={reviewData.action}
                onValueChange={(v) => setReviewData({...reviewData, action: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="approve">Approve</SelectItem>
                  <SelectItem value="reject">Reject</SelectItem>
                  <SelectItem value="request_changes">Request Changes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {reviewData.action === "approve" && (
              <>
                <div className="space-y-2">
                  <Label>Approved Budget ($)</Label>
                  <Input
                    type="number"
                    value={reviewData.approved_budget}
                    onChange={(e) => setReviewData({...reviewData, approved_budget: e.target.value})}
                    placeholder={selectedRequest?.suggested_budget?.toString() || "0"}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Estimated Completion</Label>
                  <Input
                    type="date"
                    value={reviewData.estimated_completion}
                    onChange={(e) => setReviewData({...reviewData, estimated_completion: e.target.value})}
                  />
                </div>
              </>
            )}
            
            <div className="space-y-2">
              <Label>Feedback / Notes</Label>
              <Textarea
                value={reviewData.feedback}
                onChange={(e) => setReviewData({...reviewData, feedback: e.target.value})}
                placeholder={reviewData.action === "reject" ? "Reason for rejection..." : "Optional feedback..."}
                rows={3}
              />
            </div>
            
            {reviewData.action === "approve" && (
              <div className="p-3 bg-green-50 rounded-lg text-sm text-green-700">
                <CheckCircle2 className="h-4 w-4 inline mr-1" />
                Approving will automatically create a campaign draft from this request.
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReviewDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleReviewRequest}
              className={reviewData.action === "reject" ? "bg-red-600 hover:bg-red-700" : ""}
            >
              {reviewData.action === "approve" ? "Approve" : reviewData.action === "reject" ? "Reject" : "Submit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedRequest?.title}
              <Badge className={statusConfig[selectedRequest?.status]?.color}>
                {statusConfig[selectedRequest?.status]?.label}
              </Badge>
            </DialogTitle>
            <DialogDescription>
              Request #{selectedRequest?.request_number}
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Objective</p>
                  <p className="font-medium">{objectiveLabels[selectedRequest.objective]}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Urgency</p>
                  <Badge className={urgencyConfig[selectedRequest.urgency]?.color}>
                    {selectedRequest.urgency}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Requested By</p>
                  <p className="font-medium">{selectedRequest.requester_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Budget</p>
                  <p className="font-medium">
                    {selectedRequest.approved_budget 
                      ? `$${selectedRequest.approved_budget.toLocaleString()} (approved)`
                      : selectedRequest.suggested_budget 
                        ? `$${selectedRequest.suggested_budget.toLocaleString()} (suggested)`
                        : "Not specified"
                    }
                  </p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground mb-1">Description</p>
                <p className="bg-slate-50 p-3 rounded">{selectedRequest.description}</p>
              </div>
              
              {selectedRequest.requested_channels?.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Requested Channels</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedRequest.requested_channels.map((ch) => (
                      <Badge key={ch} variant="secondary">
                        {channelOptions.find(c => c.value === ch)?.label || ch}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedRequest.segment_details?.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Target Segments</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedRequest.segment_details.map((seg) => (
                      <Badge key={seg.id} variant="outline">{seg.label || seg.name}</Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedRequest.campaign_id && (
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-700 font-medium mb-1">Campaign Created</p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate(`/marketing/campaigns/${selectedRequest.campaign_id}`)}
                  >
                    View Campaign
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </Button>
                </div>
              )}
              
              {selectedRequest.feedback_history?.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Feedback History</p>
                  <div className="space-y-2">
                    {selectedRequest.feedback_history.map((fb) => (
                      <div key={fb.id} className="p-2 bg-slate-50 rounded text-sm">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium">{fb.reviewer_name}</span>
                          <span className="text-muted-foreground">{new Date(fb.timestamp).toLocaleString()}</span>
                        </div>
                        <Badge variant="outline" className="mb-1">{fb.action}</Badge>
                        {fb.feedback && <p className="text-muted-foreground">{fb.feedback}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            {isMarketingTeam && selectedRequest?.status === "submitted" && (
              <Button onClick={() => { setShowDetailDialog(false); setShowReviewDialog(true); }}>
                Review Request
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarketingRequestsPage;
